/**
 * ngPrometPublic — the public page »Promet po conah« (#867): the switch, the
 * period caps and the route base. One module for the server (the 400 on a
 * capped period) and the page (the field errors before any request), so the
 * two numbers live in exactly one place.
 *
 * MONG's rules (Tadej Hrovat, 9. 10. 2026): only the zone view, the traffic
 * categories always summed as the internal preset, no category choice; the
 * public page may limit the periods more strictly than the internal view if
 * that is technically needed, with a notice on the page. The caps keep every
 * anonymous answer short and every export under its row cap.
 */

import type { FeatureCollection } from 'geojson';

/** The per-app switch; the public routes also require `promet`. */
export const PROMET_PUBLIC_CAPABILITY = 'promet-public';

export interface PublicPeriodLimits {
  /** Longest reference period R, in calendar days (both ends included). */
  referenceDays: number;
  /** Longest comparison period P of the day interval, in calendar days. */
  comparisonDays: number;
  /**
   * Longest window from the first day of the earlier period to the last day of
   * the later one. The chart series and the Excel source rows read that whole
   * window by the hour, the gap between R and P included.
   */
  windowDays: number;
}

/** R may be a whole year (a leap year too), P a quarter, and the two a year apart. */
export const PUBLIC_PROMET_LIMITS: PublicPeriodLimits = { referenceDays: 366, comparisonDays: 92, windowDays: 731 };

export type PublicPeriodProblemKind = 'reference' | 'comparison' | 'window';

/** Days from `from` to `to` ('YYYY-MM-DD'), both ends counted: the same day is 1. */
export function daySpan(from: string, to: string): number {
  const ms = (day: string): number => {
    const [y, m, d] = day.split('-').map(Number);
    return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  };
  return Math.round((ms(to) - ms(from)) / 86_400_000) + 1;
}

/**
 * Which public cap the periods break, with an English reason for a log line
 * and a 400 body (the page words its own text), or null. Calendar days
 * 'YYYY-MM-DD'; the hour interval's P is the day of its hour. Without P only R
 * is checked: the server calls this again once the default P is resolved.
 */
export function publicPeriodProblem(
  p: { referenceFrom: string; referenceTo: string; comparisonFrom?: string | null; comparisonTo?: string | null },
  limits: PublicPeriodLimits = PUBLIC_PROMET_LIMITS,
): { kind: PublicPeriodProblemKind; message: string } | null {
  if (daySpan(p.referenceFrom, p.referenceTo) > limits.referenceDays) {
    return { kind: 'reference', message: `the reference period may span at most ${limits.referenceDays} days` };
  }
  if (!p.comparisonFrom) return null;
  const pTo = p.comparisonTo || p.comparisonFrom;
  if (daySpan(p.comparisonFrom, pTo) > limits.comparisonDays) {
    return { kind: 'comparison', message: `the comparison period may span at most ${limits.comparisonDays} days` };
  }
  const first = p.referenceFrom < p.comparisonFrom ? p.referenceFrom : p.comparisonFrom;
  const last = p.referenceTo > pTo ? p.referenceTo : pTo;
  if (daySpan(first, last) > limits.windowDays) {
    return {
      kind: 'window',
      message: `the reference and comparison periods may span at most ${limits.windowDays} days together`,
    };
  }
  return null;
}

/** The public route family of one app: `<base>/zones`, `<base>/zones.csv`, `<base>/geometry`, … */
export function publicPrometApiBase(appPublicId: string): string {
  return `/api/v1/public/promet/${encodeURIComponent(appPublicId)}`;
}

/**
 * GET `<base>/geometry`: the app's display name and the three layers the zone
 * overlay draws. A feature's `id` is the row id the zone answer uses.
 */
export interface PublicPrometGeometry {
  name: string;
  zones: FeatureCollection;
  lines: FeatureCollection;
  points: FeatureCollection;
}
